import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of invoices from the backend.
 * @param {object} [options={}] - Optional parameters for filtering, pagination, and sorting.
 * @param {string} [options.status] - Filter by invoice status.
 * @param {number} [options.pageSize=10] - The number of invoices to fetch per page.
 * @param {string} [options.orderBy='InvoiceDate'] - Field to order by.
 * @param {'asc'|'desc'} [options.orderDirection='desc'] - Direction of ordering.
 * @param {string} [options.fseCode] - Filter by a specific franchisee Code.
 * @param {string} [options.jobId] - Filter invoices related to a specific job ID.
 * @param {string} [options.clientId] - Filter invoices related to a specific client ID.
 * @param {string} [options.householdId] - Filter invoices related to a specific household ID.
 * @param {string} [options.startAfterDocId] - The ID of the last invoice from the previous page.
 * @returns {Promise<object>} - The response data containing invoices and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listInvoices = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    pageSize = 10,
    orderBy = 'InvoiceDate',
    orderDirection = 'desc',
    fseId,
    fseCode,
    jobId,
    clientId,
    householdId,
    startAfterDocId,
    status, // Keep status here, it will be undefined if not in options
    xeroStatus,
    invoiceNumber,
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/list`;

    // Initialize body with parameters that always have a value or a non-status-specific default
    const body = { pageSize, orderBy, orderDirection };

    // Conditionally add status to the body if it was provided
    if (status !== undefined) {
      body.status = status;
    }
    if (fseId) body.fseId = fseId;
    if (fseCode) body.fseCode = fseCode;
    if (jobId) body.jobId = jobId;
    if (clientId) body.clientId = clientId;
    if (householdId) body.householdId = householdId;
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;
    if (xeroStatus) body.xeroStatus = xeroStatus;
    if (invoiceNumber) body.invoiceNumber = invoiceNumber;

    console.log("Sending request to API:", { url, body }); // Log the request

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list invoices');
    }
    return responseData.data; // Expected: { invoices: [], nextPageDocId: '...' }
  } catch (error) {
    console.error("Error listing invoices:", error);
    throw error;
  }
};

/**
 * Fetches details for a specific invoice by ID.
 * @param {string} invoiceId - The ID of the invoice to fetch.
 * @returns {Promise<object>} - The invoice data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getInvoiceDetails = async (invoiceId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!invoiceId) {
    throw new Error("Invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/details`;
    const payload = { invoiceId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to fetch invoice details');
    }

    return responseData.data; // Return the invoice data object
  } catch (error) {
    console.error(`Error fetching invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Updates editable line item values for a draft invoice.
 * @param {string} invoiceId - The Firestore invoice ID.
 * @param {{Quantity: number|string, UnitAmount: number|string}} updates - Updated line item values.
 * @returns {Promise<object>} - The response payload from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const updateInvoiceLineItem = async (invoiceId, updates) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!invoiceId) {
    throw new Error("Invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/update-line-item`;
    const payload = {
      invoiceId,
      Quantity: updates?.Quantity,
      UnitAmount: updates?.UnitAmount,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to update invoice line item');
    }

    return responseData;
  } catch (error) {
    console.error(`Error updating invoice ${invoiceId} line item:`, error);
    throw error;
  }
};

/**
 * Sends a draft invoice to Xero.
 * @param {string} draftInvoiceId - The ID of the draft invoice in Firestore.
 * @returns {Promise<object>} - The response data from the Xero API integration.
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated.
 */
export const sendDraftInvoiceToXero = async (draftInvoiceId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!draftInvoiceId) {
    throw new Error("Draft Invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/xero/invoices/from-draft`;
    const payload = { draftInvoiceId };

    console.log("Sending draft invoice to Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    // The sample response has "success": true, but the API might return a "status": "success" like other endpoints.
    // Let's be flexible or clarify the exact success field name.
    // Assuming responseData.success === true based on sample.
    if (!responseData.success) { 
        throw new Error(responseData.message || 'Failed to send invoice to Xero');
    }

    return responseData; // Returns { success: true, message: "...", data: { ...XeroInvoiceDetails... } }
  } catch (error) {
    console.error(`Error sending draft invoice ${draftInvoiceId} to Xero:`, error);
    throw error;
  }
};

/**
 * Sends multiple draft invoices to Xero.
 * @param {string[]} draftInvoiceIds - An array of draft invoice IDs.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated.
 */
export const sendMultipleDraftInvoicesToXero = async (draftInvoiceIds) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!draftInvoiceIds || draftInvoiceIds.length === 0) {
    throw new Error("Draft Invoice IDs are required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/xero/invoices/batch-from-draft`; // Assuming this is the new batch endpoint
    const payload = { draftInvoiceIds };

    console.log("Sending multiple draft invoices to Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    // The backend might return different status codes for partial success (207)
    // or full failure (400), but the body should contain the success/message fields.
    if (!response.ok && response.status !== 207 && response.status !== 400) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }
    
    // We expect 'success' (boolean) and 'message' (string) in responseData as per the examples.
    // The 'data' field will contain detailed results.
    return responseData; 

  } catch (error) {
    console.error('Error sending multiple draft invoices to Xero:', error);
    throw error;
  }
};

/**
 * Syncs an invoice with Xero to get the latest status and payment information
 * @param {string} invoiceId - The Firestore invoice ID
 * @returns {Promise<object>} - The response data containing sync status and updated fields
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const syncInvoiceWithXero = async (invoiceId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!invoiceId) {
    throw new Error("Invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/sync-with-xero`;
    const payload = { invoiceId };

    console.log("Syncing invoice with Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error(`Error syncing invoice ${invoiceId} with Xero:`, error);
    throw error;
  }
};

/**
 * Voids an invoice, optionally voiding it in Xero if already sent.
 * Also reverts the corresponding job status to approved.
 * @param {string} invoiceId - The Firestore invoice ID
 * @returns {Promise<object>} - The response data containing void status
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const voidInvoice = async (invoiceId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!invoiceId) {
    throw new Error("Invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/void`;
    const payload = { invoiceId };

    console.log("Voiding invoice, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to void invoice');
    }

    return responseData;
  } catch (error) {
    console.error(`Error voiding invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Syncs multiple invoices with Xero to get their latest status and payment information.
 * @param {string[]} invoiceIds - Array of Firestore invoice IDs to sync
 * @returns {Promise<object>} - The response containing sync results for each invoice
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const syncMultipleInvoicesWithXero = async (invoiceIds) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!invoiceIds?.length) {
    throw new Error("At least one invoice ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/invoices/sync-multiple-with-xero`;
    const payload = { invoiceIds };

    console.log('📤 Sync Request:', {
      url,
      method: 'POST',
      payload
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    // For 204 No Content, return a standardized response
    if (response.status === 204) {
      const standardResponse = {
        status: 'success',
        message: 'All invoices are up to date with Xero.',
        results: {
          totalProcessed: invoiceIds.length,
          totalUpdated: 0,
          totalFailed: 0,
          totalSkipped: invoiceIds.length,
          details: []
        },
        responseStatus: 204  // Include the HTTP status
      };
      console.log('📥 Sync Response (204):', standardResponse);
      return standardResponse;
    }

    const responseData = await response.json();
    console.log(`📥 Sync Response (${response.status}):`, responseData);

    if (!response.ok && response.status !== 207) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    // Include the HTTP status in the response
    return {
      ...responseData,
      responseStatus: response.status
    };
  } catch (error) {
    console.error('❌ Error syncing invoices with Xero:', error);
    throw error;
  }
};
